import React, { useCallback, useState } from "react";
import { Divider, Form, Modal, Spin } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import { createBundle } from "utils/api";
import { customDateFormatWithoutTime } from "components/Common/formatter";
import moment from "moment/moment";
import { USER_CURRENCY } from "utils/constants";
import NGISelect from "components/Common/inputs/NGISelect";
import { Option } from "antd/es/mentions";
import NGIInput from "components/Common/inputs/NGIInput";
import NGIDatePicker from "components/Common/inputs/NGIDatePicker";
import { TYPES } from "pages/price-management/Bundles/variables";

const useStyles = makeStyles({
  modal: {
    "& .ant-modal-content .ant-modal-body": {
      padding: "24px 50px !important",
    },
  },
  titleWithDescription: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    borderBottom: "1px solid #0000000D",
    paddingBottom: 20,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  container: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    borderLeft: "none",
    borderRight: "none",
    marginBottom: 30,
    paddingBottom: 30,
    padding: "0 50px",
  },
  tabs: {
    "&.ant-tabs > .ant-tabs-nav, .ant-tabs > div > .ant-tabs-nav": {
      padding: "0px!important",
      margin: 0,
    },
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
  },
  stepsContainer: {
    padding: "20px 50px",
    backgroundColor: "#0000000D",
    border: "1px solid #0000000D",
  },
  withMargin: {
    marginRight: 10,
  },
  typeTitle: {
    textTransform: "capitalize",
  },

  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  formItem: {
    flex: "1!important",
    margin: "0px!important",
  },
  formRow: {
    display: "flex",
    padding: "10px 0",
    gap: 15,
  },
  fieldsContainer: {},
  rowTitle: {
    flex: 1,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
  },
  subTitle: {
    fontSize: 11,
    fontWeight: 300,
    color: "#000000BF",
  },
  liteSelect: {
    "&.ant-select:not(.ant-select-customize-input) .ant-select-selector": {
      border: "2px solid #0000001A",
      borderRadius: 5,
    },
  },
  liteInput: {
    border: "2px solid #0000001A",
    borderRadius: 5,
  },
  liteDate: {
    border: "2px solid #0000001A",
    borderRadius: 5,
  },
  currency: {
    backgroundColor: "#0000000D",
    borderRadius: 2,
    fontSize: 9,
    color: "#666666",
    padding: "2px 8px",
  },
  optionValue: {
    "&>div": {
      color: "#8C8C8C",
      fontWeight: 300,
      fontSize: 9,
    },
  },
  selectedValue: {
    "& .ant-select-selection-item": {
      height: 30,
    },
  },
  disabledField: {
    flex: 1,
  },
  calculatedInput: {
    backgroundColor: "#0085FF0F!important",

    "&> input": {
      backgroundColor: "transparent!important",
    },
  },
  basePriceContainer: {
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: "#0000000A",
    padding: 15,
    fontWeight: 300,
    color: "#000000BF",
    marginBottom: 10,
  },
  amountContainer: {
    display: "flex",
    alignItems: "center",
    color: "#000000BF",
    fontWeight: 700,

    "&>span:first-child": {
      marginRight: 10,
    },
  },
});

const NewBundleModal = ({ onClose, update }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [isLoading, setLoader] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [type, setType] = useState(null);

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const onSuccessClose = useCallback(() => {
    update();
    onClose();
  }, []);

  const onCreate = useCallback(
    values => {
      const {
        type,
        name,
        displayName,
        validityPeriod,
        price,
        activationDate,
        deactivationDate,
        gasQuantity,
        cookingTime,
        gasLimit,
      } = values;

      setLoader(true);
      createBundle({
        name,
        displayName,
        type,
        validityPeriod: Number(validityPeriod),
        price: Number(price),
        activationDate: customDateFormatWithoutTime(activationDate),
        ...(deactivationDate && {
          deactivationDate: customDateFormatWithoutTime(deactivationDate),
        }),
        ...(type === "KB"
          ? {
              gasQuantity: Number(gasQuantity),
            }
          : { cookingTime: Number(cookingTime), gasLimit: Number(gasLimit) }),
      })
        .then(() => {
          setTimeout(() => {
            setLoader(false);
            successMessage("Gas bundle was created successfully");
            onClose();
            update();
          }, 500);
        })
        .catch(error => {
          setLoader(false);
          defaultNgiErrorMethod(error);
        });
    },
    [form]
  );

  return (
    <Modal
      title={
        <div className={classes.titleWithDescription}>
          Create Bundle
          <span>Please fill all mandatory fields</span>
        </div>
      }
      open
      onCancel={openConfirmationModal}
      closable={false}
      className={classes.modal}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <Form form={form} onFinish={onCreate} name="newBundle" layout="inline" className={classes.formContainer}>
          <div className={classes.fieldsContainer}>
            <div className={classes.formRow}>
              <div className={classes.rowTitle}>
                <div className={classes.title}>Bundle Name</div>
                <div className={classes.subTitle}>Give unique name</div>
              </div>
              <Form.Item
                className={classes.formItem}
                name="name"
                rules={[{ required: true, message: "Please enter value" }]}
              >
                <NGIInput placeholder="Enter Name" className={classes.liteInput} />
              </Form.Item>
            </div>
            <div className={classes.formRow}>
              <div className={classes.rowTitle}>
                <div className={classes.title}>Display Name </div>
                <div className={classes.subTitle}>Name displayed on customer’s meter</div>
              </div>
              <Form.Item
                className={classes.formItem}
                name="displayName"
                rules={[{ required: true, message: "Please enter value" }]}
              >
                <NGIInput placeholder="Enter Display Name" className={classes.liteInput} />
              </Form.Item>
            </div>
            <div className={classes.formRow}>
              <div className={classes.rowTitle}>
                <div className={classes.title}>Bundle Type</div>
                <div className={classes.subTitle}>Select between consumption or time based bundles</div>
              </div>
              <Form.Item
                className={classes.formItem}
                name="type"
                rules={[{ required: true, message: "Please enter value" }]}
              >
                <NGISelect onChange={setType} showSearch className={classes.liteSelect} placeholder="Select Type">
                  {TYPES.map(item => (
                    <Option key={`${item.value}-option`} value={item.value}>
                      {item.name}
                    </Option>
                  ))}
                </NGISelect>
              </Form.Item>
            </div>

            <Divider />

            {type === "TB" ? (
              <>
                <div className={classes.formRow}>
                  <div className={classes.rowTitle}>
                    <div className={classes.title}>Cooking Time</div>
                    <div className={classes.subTitle}>Set the cooking time in the bundle</div>
                  </div>
                  <Form.Item
                    className={classes.formItem}
                    name="cookingTime"
                    rules={[
                      { required: true, message: "Please enter value" },
                      {
                        message: "Should be a number. Allowed 2 digits after comma",
                        pattern: /^\d+(\.\d{1,2})?$/,
                      },
                    ]}
                  >
                    <NGIInput
                      placeholder="Enter Cooking Time"
                      className={classes.liteInput}
                      suffix={<span className={classes.currency}>MIN</span>}
                    />
                  </Form.Item>
                </div>
                <div className={classes.formRow}>
                  <div className={classes.rowTitle}>
                    <div className={classes.title}>Gas Limit</div>
                    <div className={classes.subTitle}>Set the upper control gas limit</div>
                  </div>
                  <Form.Item
                    className={classes.formItem}
                    name="gasLimit"
                    rules={[
                      { required: true, message: "Please enter value" },
                      {
                        message: "Should be a number. Allowed 2 digits after comma",
                        pattern: /^\d+(\.\d{1,2})?$/,
                      },
                    ]}
                  >
                    <NGIInput
                      placeholder="Enter Gas Limit"
                      className={classes.liteInput}
                      suffix={<span className={classes.currency}>KG</span>}
                    />
                  </Form.Item>
                </div>
              </>
            ) : (
              <div className={classes.formRow}>
                <div className={classes.rowTitle}>
                  <div className={classes.title}>Gas in Bundle</div>
                  <div className={classes.subTitle}>Set the quantity of gas in the bundle</div>
                </div>
                <Form.Item
                  className={classes.formItem}
                  name="gasQuantity"
                  rules={[
                    { required: true, message: "Please enter value" },
                    {
                      message: "Should be a number. Allowed 2 digits after comma",
                      pattern: /^\d+(\.\d{1,2})?$/,
                    },
                  ]}
                >
                  <NGIInput
                    placeholder="Enter value"
                    className={classes.liteInput}
                    suffix={<span className={classes.currency}>KG</span>}
                  />
                </Form.Item>
              </div>
            )}

            <div className={classes.formRow}>
              <div className={classes.rowTitle}>
                <div className={classes.title}>Bundle Price</div>
                <div className={classes.subTitle}>Set the total bundle price (for all KG’s of gas)</div>
              </div>
              <Form.Item
                className={classes.formItem}
                name="price"
                rules={[
                  { required: true, message: "Please enter value" },
                  {
                    message: "Should be a number. Allowed 2 digits after comma",
                    pattern: /^\d+(\.\d{1,2})?$/,
                  },
                ]}
              >
                <NGIInput
                  placeholder="Enter price"
                  className={classes.liteInput}
                  suffix={<span className={classes.currency}>{USER_CURRENCY()}</span>}
                />
              </Form.Item>
            </div>
            <div className={classes.formRow}>
              <div className={classes.rowTitle}>
                <div className={classes.title}>Bundle Duration Period</div>
                <div className={classes.subTitle}>
                  Set the time (in days) the bundle will be active after activation
                </div>
              </div>
              <Form.Item
                className={classes.formItem}
                name="validityPeriod"
                rules={[
                  { required: true, message: "Please enter value" },
                  {
                    message: "Should be a integer",
                    pattern: /^\d+$/,
                  },
                ]}
              >
                <NGIInput
                  placeholder="Enter Duration Period"
                  className={classes.liteInput}
                  suffix={<span className={classes.currency}>DAYS</span>}
                />
              </Form.Item>
            </div>

            <div className={classes.formRow}>
              <div className={classes.rowTitle}>
                <div className={classes.title}>Bundle Start Date</div>
                <div className={classes.subTitle}>Set the first day this bundle will be available for activation</div>
              </div>
              <Form.Item
                className={classes.formItem}
                name="activationDate"
                rules={[{ required: true, message: "Please select date" }]}
              >
                <NGIDatePicker
                  onChange={() => {
                    form.setFieldValue("deactivationDate", null);
                  }}
                  disableDates={current => moment() >= current}
                  className={classes.liteInput}
                  placeholder="Bundle Start Date"
                />
              </Form.Item>
            </div>
            <div className={classes.formRow}>
              <div className={classes.rowTitle}>
                <div className={classes.title}>Bundle End Date</div>
                <div className={classes.subTitle}>Set the last day this bundle will be available for activation</div>
              </div>
              <Form.Item className={classes.formItem} name="deactivationDate">
                <NGIDatePicker
                  allowClear
                  disableDates={current =>
                    form.getFieldValue("activationDate")?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) >
                    current
                  }
                  className={classes.liteInput}
                  placeholder="Bundle End Date"
                />
              </Form.Item>
            </div>
          </div>

          <Divider />

          <div className={classes.actions}>
            <CustomButton onClick={openConfirmationModal} type="primary" size="small" color="outlined" text="Cancel" />

            <CustomButton size="small" text="CREATE BUNDLE" type="primary" />
          </div>
        </Form>
      </Spin>

      {isConfirmationOpen && (
        <ConfirmationModal
          description="The Bundle creation is in progress. Are you sure you want to discard the changes?"
          onCancel={closeConfirmationModal}
          onConfirm={onSuccessClose}
        />
      )}
    </Modal>
  );
};

export default NewBundleModal;
